/**
 * city/textures/textures-interior-signs.js
 * Texture nội thất nhìn qua cửa kính và biển hiệu, biển số xe đô thị Việt Nam
 */

import { createRng, shade } from '../builder-core.js';
import { CANVAS_FONTS } from '../../core/fonts.js';

class MethodsHolder {
  // Nội thất nhìn qua cửa: 'pho' | 'cafe' | 'pharmacy' | 'showroom' | 'living'
  getInteriorTexture(kind = 'pho', seed = 1) {
    return this._tex(`interior|${kind}|${seed}`, 256, 192, (ctx, w, h) => {
      const rnd = createRng(seed + 5);
      const grad = (stops) => {
        const g = ctx.createLinearGradient(0, 0, 0, h);
        stops.forEach(([p, c]) => g.addColorStop(p, c));
        ctx.fillStyle = g;
        ctx.fillRect(0, 0, w, h);
      };

      if (kind === 'pho') {
        grad([
          [0, '#fff1c9'],
          [0.55, '#e8b774'],
          [1, '#6b3f1d']
        ]);
        ctx.fillStyle = 'rgba(255,255,230,0.95)';
        [0.2, 0.5, 0.8].forEach((p) => ctx.fillRect(w * p - 14, 6, 28, 5));
        ctx.fillStyle = '#b91c1c';
        ctx.fillRect(w * 0.32, 20, w * 0.4, 46);
        ctx.fillStyle = '#fde047';
        for (let i = 0; i < 4; i++) ctx.fillRect(w * 0.35, 28 + i * 9, w * 0.34 * (0.5 + 0.5 * rnd()), 4);
        // Tủ kính & nồi nước dùng
        ctx.fillStyle = 'rgba(226,232,240,0.9)';
        ctx.fillRect(6, h * 0.55, 74, h * 0.45);
        ctx.fillStyle = '#94a3b8';
        ctx.fillRect(18, h * 0.42, 48, 26);
        ctx.fillStyle = 'rgba(255,255,255,0.4)';
        for (let i = 0; i < 4; i++) {
          ctx.beginPath();
          ctx.arc(30 + i * 8, h * 0.36 - i * 7, 6 + i, 0, Math.PI * 2);
          ctx.fill();
        }
        // Bàn gỗ & ghế nhựa
        for (let i = 0; i < 2; i++) {
          const x = 112 + i * 72;
          ctx.fillStyle = '#78350f';
          ctx.fillRect(x, h * 0.66, 56, 8);
          ctx.fillRect(x + 4, h * 0.66, 5, h * 0.3);
          ctx.fillRect(x + 47, h * 0.66, 5, h * 0.3);
          ctx.fillStyle = i ? '#2563eb' : '#dc2626';
          ctx.fillRect(x - 12, h * 0.82, 16, 6);
          ctx.fillRect(x + 52, h * 0.82, 16, 6);
        }
      } else if (kind === 'cafe') {
        grad([
          [0, '#3b2415'],
          [0.6, '#6b4226'],
          [1, '#2a180d']
        ]);
        for (let i = 0; i < 7; i++) {
          const x = 18 + i * 36,
            y = 16 + Math.sin(i) * 5;
          const g = ctx.createRadialGradient(x, y, 1, x, y, 16);
          g.addColorStop(0, 'rgba(254,240,138,1)');
          g.addColorStop(1, 'rgba(254,240,138,0)');
          ctx.fillStyle = g;
          ctx.fillRect(x - 16, y - 16, 32, 32);
        }
        ctx.fillStyle = '#1c1917';
        ctx.fillRect(18, 40, 90, 62);
        ctx.fillStyle = 'rgba(255,255,255,0.75)';
        for (let i = 0; i < 5; i++) ctx.fillRect(26, 50 + i * 10, 40 + rnd() * 30, 3);
        ctx.fillStyle = '#8b5a2b';
        [62, 92].forEach((y) => ctx.fillRect(140, y, 104, 4));
        ctx.fillStyle = '#f5f5f4';
        for (let i = 0; i < 8; i++) ctx.fillRect(146 + i * 12, 50, 7, 11);
        for (let i = 0; i < 6; i++) ctx.fillRect(148 + i * 16, 80, 9, 11);
        ctx.fillStyle = '#8b5a2b';
        ctx.fillRect(0, h * 0.72, w, h * 0.28);
        ctx.fillStyle = '#a8703f';
        ctx.fillRect(0, h * 0.72, w, 5);
        ctx.fillStyle = '#d4d4d8';
        ctx.fillRect(176, h * 0.58, 40, 26);
      } else if (kind === 'pharmacy') {
        grad([
          [0, '#ffffff'],
          [1, '#dbeafe']
        ]);
        ctx.fillStyle = '#16a34a';
        ctx.fillRect(w / 2 - 5, 10, 10, 30);
        ctx.fillRect(w / 2 - 15, 20, 30, 10);
        const palette = ['#ffffff', '#bfdbfe', '#86efac', '#fca5a5', '#fde68a', '#93c5fd'];
        for (let r = 0; r < 3; r++) {
          const y = 72 + r * 30;
          ctx.fillStyle = '#94a3b8';
          ctx.fillRect(8, y, w - 16, 3);
          let x = 10;
          while (x < w - 20) {
            const bw = 8 + rnd() * 10,
              bh = 12 + rnd() * 12;
            ctx.fillStyle = palette[Math.floor(rnd() * palette.length)];
            ctx.fillRect(x, y - bh, bw, bh);
            ctx.strokeStyle = 'rgba(71,85,105,0.35)';
            ctx.strokeRect(x, y - bh, bw, bh);
            x += bw + 2;
          }
        }
        ctx.fillStyle = '#f8fafc';
        ctx.fillRect(0, h * 0.8, w, h * 0.2);
        ctx.fillStyle = '#16a34a';
        ctx.fillRect(0, h * 0.8, w, 5);
      } else {
        const warm = kind === 'living';
        grad(
          warm
            ? [
                [0, '#fef3c7'],
                [0.7, '#e7d3b0'],
                [1, '#8a6d4b']
              ]
            : [
                [0, '#f5f5f4'],
                [0.7, '#d6d3d1'],
                [1, '#78716c']
              ]
        );
        ctx.fillStyle = 'rgba(255,255,240,0.9)';
        [0.25, 0.5, 0.75].forEach((p) => ctx.fillRect(w * p - 6, 4, 12, 4));
        ctx.fillStyle = '#1e3a8a';
        ctx.fillRect(w * 0.55, 36, 64, 42);
        ctx.fillStyle = '#fbbf24';
        ctx.fillRect(w * 0.55 + 10, 50, 22, 18);
        ctx.fillStyle = warm ? '#92400e' : '#64748b';
        ctx.fillRect(w * 0.12, h * 0.5, 120, 20);
        ctx.fillRect(w * 0.1, h * 0.6, 130, 26);
        ctx.fillStyle = warm ? '#b45309' : '#94a3b8';
        ctx.fillRect(w * 0.14, h * 0.6, 40, 12);
        ctx.fillRect(w * 0.14 + 46, h * 0.6, 40, 12);
        ctx.fillStyle = '#1f2937';
        ctx.fillRect(w * 0.86, h * 0.38, 3, h * 0.38);
        ctx.fillStyle = '#fde68a';
        ctx.fillRect(w * 0.86 - 10, h * 0.33, 23, 12);
        ctx.fillStyle = '#15803d';
        for (let i = 0; i < 6; i++) {
          ctx.beginPath();
          ctx.arc(20 + rnd() * 14, h * 0.55 + rnd() * 30, 9, 0, Math.PI * 2);
          ctx.fill();
        }
      }
    });
  }

  // Biển hiệu: chữ tự co vừa khung, có dòng phụ (SĐT/địa chỉ) và icon chữ thập nhà thuốc
  getSignTexture(opts = {}) {
    const {
      text = '',
      sub = '',
      bg = '#b91c1c',
      fg = '#ffffff',
      border = null,
      font = CANVAS_FONTS.sans,
      icon = null,
      aspect = 4
    } = opts;
    const cw = Math.max(256, Math.min(1024, Math.round(128 * aspect)));
    const key = `sign|${text}|${sub}|${bg}|${fg}|${border}|${font}|${icon}|${cw}`;
    return this._tex(key, cw, 128, (ctx, w, h) => {
      const g = ctx.createLinearGradient(0, 0, 0, h);
      g.addColorStop(0, shade(bg, 0.14));
      g.addColorStop(1, shade(bg, -0.18));
      ctx.fillStyle = g;
      ctx.fillRect(0, 0, w, h);
      if (border) {
        ctx.strokeStyle = border;
        ctx.lineWidth = 7;
        ctx.strokeRect(8, 8, w - 16, h - 16);
      }
      let cx = w / 2;
      let avail = w - 52;
      if (icon === 'cross') {
        const ix = h / 2 + 6;
        ctx.fillStyle = '#ffffff';
        ctx.beginPath();
        ctx.arc(ix, h / 2, 40, 0, Math.PI * 2);
        ctx.fill();
        ctx.fillStyle = '#16a34a';
        ctx.fillRect(ix - 9, h / 2 - 28, 18, 56);
        ctx.fillRect(ix - 28, h / 2 - 9, 56, 18);
        cx += h / 2;
        avail -= h;
      }
      ctx.fillStyle = fg;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      let size = sub ? 56 : 68;
      do {
        ctx.font = `bold ${size}px ${font}`;
        size -= 2;
      } while (ctx.measureText(text).width > avail && size > 14);
      ctx.fillText(text, cx, sub ? h * 0.4 : h / 2);
      if (sub) {
        ctx.globalAlpha = 0.9;
        let subSize = 22;
        do {
          ctx.font = `600 ${subSize}px ${CANVAS_FONTS.sans}`;
          subSize -= 1;
        } while (ctx.measureText(sub).width > avail && subSize > 10);
        ctx.fillText(sub, cx, h * 0.8);
        ctx.globalAlpha = 1;
      }
    });
  }

  // Biển số xe: 1 dòng (ô tô) hoặc 2 dòng tách bằng '\n' (xe máy)
  getPlateTexture(text, bg = '#f8fafc') {
    const lines = text.split('\n');
    const cw = lines.length > 1 ? 160 : 256;
    const ch = lines.length > 1 ? 120 : 64;
    return this._tex(`plate|${text}|${bg}`, cw, ch, (ctx, w, h) => {
      ctx.fillStyle = bg;
      ctx.fillRect(0, 0, w, h);
      ctx.strokeStyle = '#111827';
      ctx.lineWidth = 4;
      ctx.strokeRect(3, 3, w - 6, h - 6);
      ctx.fillStyle = '#111827';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.font = `bold ${lines.length > 1 ? 44 : 40}px ${CANVAS_FONTS.sans}`;
      lines.forEach((ln, i) => ctx.fillText(ln, w / 2, (h / lines.length) * (i + 0.55)));
    });
  }
}

export function registerTextureInteriorSignsMethods(proto) {
  Object.getOwnPropertyNames(MethodsHolder.prototype).forEach((name) => {
    if (name !== 'constructor') {
      proto[name] = MethodsHolder.prototype[name];
    }
  });
}
